import { useState, useCallback, useMemo, useReducer } from "react";
import { useForm } from "react-hook-form";
import { Timestamp } from "firebase/firestore";
import { RECORD_CONSTANTS } from "../../../constants";

const MAX_AI_PROMPT_LENGTH = 500;

const createValidationError = (i18nKey, values = {}, defaultValue = i18nKey) => ({
  i18nKey,
  values,
  defaultValue,
});

const createInitialAiState = () => ({
  status: "idle",
  prompt: "",
  proposal: undefined,
  message: undefined,
  lastError: undefined,
});

function aiAssistantReducer(state, action) {
  switch (action.type) {
    case "setPrompt": {
      const nextPrompt = (action.prompt || "").slice(0, MAX_AI_PROMPT_LENGTH);
      return {
        ...state,
        prompt: nextPrompt,
      };
    }
    case "startRequest": {
      const nextPrompt =
        typeof action.prompt === "string"
          ? action.prompt.slice(0, MAX_AI_PROMPT_LENGTH)
          : state.prompt;
      return {
        status: "loading",
        prompt: nextPrompt,
        proposal: undefined,
        message: undefined,
        lastError: undefined,
      };
    }
    case "setProposal": {
      return {
        status: "proposalReady",
        prompt: state.prompt,
        proposal: action.proposal,
        message: action.message,
        lastError: undefined,
      };
    }
    case "setError": {
      return {
        ...state,
        status: "error",
        lastError: action.error,
        proposal: undefined,
        message: undefined,
      };
    }
    case "clear": {
      const keepPrompt = Boolean(action.keepPrompt);
      return {
        status: "idle",
        prompt: keepPrompt ? state.prompt : "",
        proposal: undefined,
        message: undefined,
        lastError: undefined,
      };
    }
    default:
      return state;
  }
}

const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  !(value instanceof Date);

const normalizeAiDateRangePayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const { start, end, timezone, isAllDay } = payload;
  const startIso = typeof start === "string" ? start : null;
  const endIso = typeof end === "string" ? end : null;
  const timezoneValue =
    typeof timezone === "string" && timezone.trim() ? timezone.trim() : null;

  if (!startIso || !endIso || !timezoneValue) {
    return null;
  }

  const startDate = new Date(startIso);
  const endDate = new Date(endIso);

  if (
    !Number.isFinite(startDate.getTime()) ||
    !Number.isFinite(endDate.getTime()) ||
    endDate <= startDate
  ) {
    return null;
  }

  try {
    return {
      start: Timestamp.fromDate(startDate),
      end: Timestamp.fromDate(endDate),
      timezone: timezoneValue,
      isAllDay: Boolean(isAllDay),
    };
  } catch (error) {
    console.warn("Failed to normalise AI date proposal:", error);
    return null;
  }
};

const normalizeAiDueDate = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (
    typeof value === "object" &&
    typeof value.seconds === "number" &&
    typeof value.nanoseconds === "number"
  ) {
    try {
      const millis =
        value.seconds * 1000 + Math.round(value.nanoseconds / 1e6);
      const date = new Date(millis);
      if (Number.isFinite(date.getTime())) {
        return Timestamp.fromDate(date);
      }
    } catch (error) {
      console.warn("Failed to normalise AI due date proposal:", error);
    }
    return null;
  }

  if (value instanceof Timestamp) {
    return value;
  }

  if (value instanceof Date) {
    if (Number.isFinite(value.getTime())) {
      return Timestamp.fromDate(value);
    }
    return null;
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (Number.isFinite(parsed.getTime())) {
      return Timestamp.fromDate(parsed);
    }
    return null;
  }

  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      const dateValue = value.toDate();
      if (Number.isFinite(dateValue?.getTime())) {
        return Timestamp.fromDate(dateValue);
      }
    } catch (error) {
      console.warn("Failed to normalise AI due date proposal:", error);
    }
    return null;
  }

  return null;
};

const normalizeAiLocation = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const { name, lat, lng, source, placeId, context, geohash } = value;
  const trimmedName = typeof name === "string" ? name.trim() : "";
  const numericLat =
    typeof lat === "number"
      ? lat
      : typeof lat === "string"
      ? Number.parseFloat(lat)
      : NaN;
  const numericLng =
    typeof lng === "number"
      ? lng
      : typeof lng === "string"
      ? Number.parseFloat(lng)
      : NaN;
  const normalizedSource =
    typeof source === "string" ? source.trim().toLowerCase() : "";

  if (!trimmedName) {
    return null;
  }

  if (!Number.isFinite(numericLat) || numericLat < -90 || numericLat > 90) {
    return null;
  }

  if (!Number.isFinite(numericLng) || numericLng < -180 || numericLng > 180) {
    return null;
  }

  if (!["google", "mapbox", "manual"].includes(normalizedSource)) {
    return null;
  }

  const nextLocation = {
    name: trimmedName,
    lat: numericLat,
    lng: numericLng,
    source: normalizedSource,
  };

  if (typeof placeId === "string" && placeId.trim()) {
    nextLocation.placeId = placeId.trim();
  }

  if (typeof context === "string" && context.trim()) {
    nextLocation.context = context.trim();
  }

  if (typeof geohash === "string" && geohash.trim()) {
    nextLocation.geohash = geohash.trim();
  }

  return nextLocation;
};

const normalizeAiLinkEntry = (value, index) => {
  if (!value || typeof value !== "object") {
    console.warn("Skipping AI link proposal with invalid entry:", value);
    return null;
  }

  const targetId =
    typeof value.targetId === "string" && value.targetId.trim()
      ? value.targetId.trim()
      : null;
  const rawTargetType =
    typeof value.targetType === "string" && value.targetType.trim()
      ? value.targetType.trim().toLowerCase()
      : null;
  const targetType =
    rawTargetType && (rawTargetType === "record" || rawTargetType === "external")
      ? rawTargetType
      : null;

  if (!targetId || !targetType) {
    console.warn(
      "Skipping AI link proposal missing required fields:",
      value,
      "at index",
      index
    );
    return null;
  }

  let addedAt = value.addedAt;
  if (addedAt instanceof Date) {
    if (!Number.isFinite(addedAt.getTime())) {
      addedAt = null;
    }
  } else if (addedAt instanceof Timestamp) {
    try {
      const derived = addedAt.toDate();
      addedAt = Number.isFinite(derived?.getTime()) ? derived : null;
    } catch (error) {
      console.warn("Failed to normalise AI link timestamp:", error);
      addedAt = null;
    }
  } else if (
    addedAt &&
    typeof addedAt === "object" &&
    typeof addedAt.toDate === "function"
  ) {
    try {
      const derived = addedAt.toDate();
      addedAt = Number.isFinite(derived?.getTime()) ? derived : null;
    } catch (error) {
      console.warn("Failed to normalise AI link timestamp:", error);
      addedAt = null;
    }
  } else if (
    addedAt &&
    typeof addedAt === "object" &&
    typeof addedAt.seconds === "number" &&
    typeof addedAt.nanoseconds === "number"
  ) {
    try {
      const millis =
        addedAt.seconds * 1000 + Math.floor(addedAt.nanoseconds / 1e6);
      const derived = new Date(millis);
      addedAt = Number.isFinite(derived.getTime()) ? derived : null;
    } catch (error) {
      console.warn("Failed to normalise AI link timestamp:", error);
      addedAt = null;
    }
  } else if (typeof addedAt === "string" || typeof addedAt === "number") {
    const parsed = new Date(addedAt);
    addedAt = Number.isFinite(parsed.getTime()) ? parsed : null;
  } else {
    addedAt = null;
  }

  if (!(addedAt instanceof Date) || !Number.isFinite(addedAt.getTime())) {
    addedAt = new Date();
  }

  const normalized = {
    targetId,
    targetType,
    addedAt,
  };

  const targetExcerpt =
    typeof value.targetExcerpt === "string" && value.targetExcerpt.trim()
      ? value.targetExcerpt.trim()
      : null;
  if (targetExcerpt) {
    normalized.targetExcerpt = targetExcerpt;
  }

  if (value.targetAuthor && typeof value.targetAuthor === "object") {
    const author = {};
    if (
      typeof value.targetAuthor.name === "string" &&
      value.targetAuthor.name.trim()
    ) {
      author.name = value.targetAuthor.name.trim();
    }
    if (
      typeof value.targetAuthor.avatar === "string" &&
      value.targetAuthor.avatar.trim()
    ) {
      author.avatar = value.targetAuthor.avatar.trim();
    }
    if (Object.keys(author).length > 0) {
      normalized.targetAuthor = author;
    }
  }

  Object.entries(value).forEach(([key, extraValue]) => {
    if (
      key === "targetId" ||
      key === "targetType" ||
      key === "addedAt" ||
      key === "targetExcerpt" ||
      key === "targetAuthor"
    ) {
      return;
    }
    normalized[key] = extraValue;
  });

  return normalized;
};

const normalizeAiLinksOutgoing = (value) => {
  if (!Array.isArray(value)) {
    return null;
  }

  const normalized = value
    .map((entry, index) => normalizeAiLinkEntry(entry, index))
    .filter(Boolean);

  if (!normalized.length && value.length > 0) {
    console.warn("Skipping AI links proposal with no valid entries:", value);
    return null;
  }

  return normalized;
};

export function deriveAiOverlay(proposal) {
  if (!proposal) {
    return {};
  }

  const overlay = {};

  const walk = (basePath, value) => {
    if (!isPlainObject(value)) {
      overlay[basePath] = {
        value,
        isRemoval: value === null,
        source: "ai",
      };
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      const nextPath = basePath ? `${basePath}.${key}` : key;
      if (isPlainObject(nestedValue)) {
        walk(nextPath, nestedValue);
      } else {
        overlay[nextPath] = {
          value: nestedValue,
          isRemoval: nestedValue === null,
          source: "ai",
        };
      }
    });
  };

  if (proposal.content) {
    Object.entries(proposal.content).forEach(([key, value]) => {
      const path = `content.${key}`;
      if (isPlainObject(value)) {
        walk(path, value);
      } else {
        overlay[path] = {
          value,
          isRemoval: value === null,
          source: "ai",
        };
      }
    });
  }

  if (proposal.attributes) {
    Object.entries(proposal.attributes).forEach(([key, value]) => {
      const path = `attributes.${key}`;
      if (key === "date" || key === "location") {
        overlay[path] = {
          value,
          isRemoval: value === null,
          source: "ai",
        };
        return;
      }
      if (isPlainObject(value)) {
        walk(path, value);
      } else {
        overlay[path] = {
          value,
          isRemoval: value === null,
          source: "ai",
        };
      }
    });
  }

  if (proposal.links) {
    Object.entries(proposal.links).forEach(([key, value]) => {
      const path = `links.${key}`;
      if (isPlainObject(value)) {
        walk(path, value);
      } else {
        overlay[path] = {
          value,
          isRemoval: value === null,
          source: "ai",
        };
      }
    });
  }

  return overlay;
}

const getValueAtPath = (data, path) => {
  return path.split(".").reduce((acc, key) => {
    if (acc === undefined || acc === null) {
      return undefined;
    }
    return acc[key];
  }, data);
};

const getClearedValue = (currentValue) => {
  if (Array.isArray(currentValue)) {
    return [];
  }
  if (typeof currentValue === "string") {
    return "";
  }
  if (currentValue instanceof Date) {
    return null;
  }

  if (
    currentValue &&
    typeof currentValue === "object" &&
    typeof currentValue.toDate === "function"
  ) {
    return null;
  }

  return null;
};

// Pure function to compute active views based on form data
function computeActiveViews(formData) {
  const views = new Set([RECORD_CONSTANTS.MANDATORY_VIEW]); // Note is always active

  const taskAttributes = formData?.attributes || {};
  
  // Task view - active if isComplete exists (even if false)
  if (
    taskAttributes.isComplete !== undefined &&
    taskAttributes.isComplete !== null
  ) {
    views.add(RECORD_CONSTANTS.VIEW_TYPES.TASK);
  } else if (taskAttributes.dueDate) {
    views.add(RECORD_CONSTANTS.VIEW_TYPES.TASK);
  } else if (
    taskAttributes.priority === "high" ||
    taskAttributes.priority === "medium" ||
    taskAttributes.priority === "low"
  ) {
    views.add(RECORD_CONSTANTS.VIEW_TYPES.TASK);
  }
  
  // Calendar view - active if date exists (DateRange object)
  if (formData.attributes.date !== undefined && 
      formData.attributes.date !== null &&
      formData.attributes.date.start &&
      formData.attributes.date.end) {
    views.add(RECORD_CONSTANTS.VIEW_TYPES.CALENDAR);
  }
  
  // Map view - active if location exists
  if (formData.attributes.location !== undefined && formData.attributes.location !== null) {
    views.add(RECORD_CONSTANTS.VIEW_TYPES.MAP);
  }
  
  // Gallery view - active if attachments exist
  if (formData.content.attachments.length > 0) {
    views.add(RECORD_CONSTANTS.VIEW_TYPES.GALLERY);
  }
  
  // Graph view - active if linked records exist
  if (formData.links.outgoing.length > 0) {
    views.add(RECORD_CONSTANTS.VIEW_TYPES.GRAPH);
  }
  
  return views;
}

const buildDefaultValues = (record, initialLinks = []) => {
  const linksSource = Array.isArray(record?.links?.outgoing)
    ? record.links.outgoing
    : Array.isArray(initialLinks)
    ? initialLinks
    : [];

  return {
    content: {
      title: record?.content?.title || "",
      body: record?.content?.body || "",
      tags: record?.content?.tags || [],
      attachments: record?.content?.attachments || [],
    },
    attributes: record?.attributes || {},
    access: {
      visibility: record?.access?.visibility || "private",
    },
    links: {
      outgoing: linksSource.map((link) => ({
        ...link,
      })),
    },
  };
};

export function useRecordForm(record, options = {}) {
  const { initialLinks } = options;
  const isEditMode = !!record;
  const prefillLinks = !isEditMode && Array.isArray(initialLinks) ? initialLinks : null;
  const defaultValues = useMemo(
    () => buildDefaultValues(record, prefillLinks || []),
    [record, prefillLinks]
  );
  
  // Track original attachments for edit mode to compute deletions
  // This will be updated when attachments are hydrated
  // Initialize as empty array since attachments are loaded asynchronously
  const [originalAttachments, setOriginalAttachments] = useState(() => {
    // Start with empty array - will be populated when attachments are hydrated
    return [];
  });

  // Initialize React Hook Form with default values
  const {
    watch,
    setValue,
    formState: { errors: rhfErrors, isDirty },
    trigger,
    reset: rhfReset,
  } = useForm({
    defaultValues,
    mode: "onChange", // Validate on change for immediate feedback
  });

  // Watch all form values for reactive updates
  const formData = watch();

  // Track AI workspace payload for new records
  const [ai, setAi] = useState(null);
  const [aiAssistantState, dispatchAiAssistant] = useReducer(
    aiAssistantReducer,
    undefined,
    createInitialAiState
  );

  // Compute attachment changes separately from React Hook Form's dirty state
  const hasAttachmentChanges = useMemo(() => {
    // For new records, any attachments mean changes
    if (!isEditMode) {
      return formData.content.attachments.length > 0;
    }
    
    // For existing records, compare current attachments against original
    const currentIds = new Set(formData.content.attachments.map(att => att.id));
    const originalIds = new Set(originalAttachments.map(att => att.id));
    
    // Check if sets are different (additions or deletions)
    if (currentIds.size !== originalIds.size) return true;
    
    // Check for any IDs in current that aren't in original
    for (const id of currentIds) {
      if (!originalIds.has(id)) return true;
    }
    
    // Check for any IDs in original that aren't in current (deletions)
    for (const id of originalIds) {
      if (!currentIds.has(id)) return true;
    }
    
    return false;
  }, [formData.content.attachments, originalAttachments, isEditMode]);

  // Combine React Hook Form's isDirty with attachment change detection
  const hasChanges = isDirty || hasAttachmentChanges;

  // Compute active views based on data presence (data-driven state)
  // Using useMemo to prevent recomputation on every render
  const userSelectedViews = useMemo(
    () => computeActiveViews(formData),
    [formData]
  );

  // Custom validation errors (in addition to RHF errors)
  const [customErrors, setCustomErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  

  // Merge React Hook Form errors with custom errors
  const errors = { ...rhfErrors, ...customErrors };

  const setAiPrompt = useCallback(
    (prompt) => {
      dispatchAiAssistant({ type: "setPrompt", prompt });
    },
    [dispatchAiAssistant]
  );

  const setAiStatus = useCallback(
    (status, options = {}) => {
      switch (status) {
        case "idle": {
          dispatchAiAssistant({
            type: "clear",
            keepPrompt: Boolean(options.keepPrompt),
          });
          break;
        }
        case "loading": {
          dispatchAiAssistant({
            type: "startRequest",
            prompt: options.prompt,
          });
          break;
        }
        case "error": {
          if (!options.error) return;
          dispatchAiAssistant({
            type: "setError",
            error: options.error,
          });
          break;
        }
        default:
          break;
      }
    },
    [dispatchAiAssistant]
  );

  const setAiProposal = useCallback(
    (payload) => {
      if (!payload || !payload.proposal) {
        dispatchAiAssistant({ type: "clear", keepPrompt: false });
        return;
      }

      const { proposal, message } = payload;

      dispatchAiAssistant({
        type: "setProposal",
        proposal,
        message,
      });
    },
    [dispatchAiAssistant]
  );

  const clearAiProposal = useCallback(() => {
    dispatchAiAssistant({ type: "clear", keepPrompt: true });
  }, [dispatchAiAssistant]);

  const aiOverlay = useMemo(
    () => deriveAiOverlay(aiAssistantState.proposal),
    [aiAssistantState.proposal]
  );

  const aiProposal = aiAssistantState.proposal;
  const aiPrompt = aiAssistantState.prompt;

  const applyAiProposal = useCallback(() => {
    if (!aiProposal) return;

    const latestFormValues = watch();
    const setValueOptions = {
      shouldDirty: true,
      shouldValidate: true,
    };
    let appliedDueDate = false;
    let appliedPriority = false;
    let appliedIsComplete = false;

    Object.entries(aiOverlay).forEach(([path, entry]) => {
      if (path === "attributes.date") {
        const currentValue = getValueAtPath(latestFormValues, path);
        if (entry.isRemoval) {
          const nextValue = getClearedValue(currentValue);
          setValue(path, nextValue, setValueOptions);
          return;
        }

        const normalizedDateRange = normalizeAiDateRangePayload(entry.value);
        if (!normalizedDateRange) {
          console.warn("Skipping malformed AI date proposal:", entry.value);
          return;
        }

        setValue(path, normalizedDateRange, setValueOptions);
        return;
      }

      if (path === "links.outgoing") {
        if (entry.isRemoval) {
          setValue(path, [], setValueOptions);
          return;
        }

        const normalizedLinks = normalizeAiLinksOutgoing(entry.value);
        if (!normalizedLinks) {
          return;
        }

        setValue(path, normalizedLinks, setValueOptions);
        return;
      }

      if (path === "attributes.dueDate") {
        if (entry.isRemoval) {
          setValue(path, null, setValueOptions);
          return;
        }

        const normalizedDueDate = normalizeAiDueDate(entry.value);
        if (!normalizedDueDate) {
          console.warn("Skipping malformed AI due date proposal:", entry.value);
          return;
        }

        setValue(path, normalizedDueDate, setValueOptions);
        appliedDueDate = true;
        return;
      }

      if (path === "attributes.isComplete") {
        appliedIsComplete = true;

        if (entry.isRemoval) {
          setValue(path, null, setValueOptions);
          return;
        }

        if (typeof entry.value !== "boolean") {
          console.warn(
            "Skipping non-boolean AI isComplete proposal:",
            entry.value
          );
          return;
        }

        setValue(path, entry.value, setValueOptions);
        return;
      }

      if (path === "attributes.priority") {
        if (entry.isRemoval) {
          setValue(path, null, setValueOptions);
          return;
        }

        const nextPriority =
          typeof entry.value === "string"
            ? entry.value.trim().toLowerCase()
            : null;

        if (!nextPriority) {
          console.warn("Skipping empty AI priority proposal:", entry.value);
          return;
        }

        if (!["high", "medium", "low"].includes(nextPriority)) {
          console.warn("Skipping invalid AI priority proposal:", entry.value);
          return;
        }

        setValue(path, nextPriority, setValueOptions);
        appliedPriority = true;
        return;
      }

      if (path === "attributes.location") {
        if (entry.isRemoval) {
          setValue(path, null, setValueOptions);
          return;
        }

        const normalizedLocation = normalizeAiLocation(entry.value);
        if (!normalizedLocation) {
          console.warn(
            "Skipping malformed AI location proposal:",
            entry.value
          );
          return;
        }

        setValue(path, normalizedLocation, setValueOptions);
        return;
      }

      const currentValue = getValueAtPath(latestFormValues, path);
      const nextValue = entry.isRemoval
        ? getClearedValue(currentValue)
        : entry.value;

      setValue(path, nextValue, setValueOptions);
    });

    if (
      !appliedIsComplete &&
      (appliedDueDate || appliedPriority)
    ) {
      const currentIsComplete = getValueAtPath(
        latestFormValues,
        "attributes.isComplete"
      );
      if (currentIsComplete === undefined || currentIsComplete === null) {
        setValue("attributes.isComplete", false, setValueOptions);
      }
    }

    if (!isEditMode && aiPrompt) {
      const trimmedPrompt = aiPrompt.slice(0, 5000);
      setAi({
        notes: trimmedPrompt,
        updatedAt: new Date(),
      });
    }

    dispatchAiAssistant({ type: "clear", keepPrompt: false });
  }, [
    aiProposal,
    aiOverlay,
    watch,
    isEditMode,
    setValue,
    aiPrompt,
    dispatchAiAssistant,
    setAi,
  ]);

  const updateField = useCallback((path, value) => {
    // Special handling for original attachments tracking
    if (path === '_originalAttachments') {
      setOriginalAttachments(value);
      return;
    }
    
    // Use React Hook Form's setValue for optimal performance
    setValue(path, value, { shouldDirty: true, shouldValidate: true });

    // Clear custom error for this field if it exists
    setCustomErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[path];
      return newErrors;
    });
  }, [setValue]);

  // Create default data for task view
  const createDefaultTaskData = useCallback(() => {
    setValue('attributes.isComplete', false, { shouldDirty: true });
  }, [setValue]);
  
  // Create default data for calendar view
  const createDefaultCalendarData = useCallback(() => {
    // Set next hour on the hour as default
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0);
    
    // Create end time one hour after start
    const endTime = new Date(nextHour);
    endTime.setHours(nextHour.getHours() + 1);
    
    // Get browser timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create DateRange object
    const dateRange = {
      start: Timestamp.fromDate(nextHour),
      end: Timestamp.fromDate(endTime),
      timezone: timezone,
      isAllDay: false
    };
    
    setValue('attributes.date', dateRange, { shouldDirty: true });
  }, [setValue]);
  
  // Create default data for map view
  const createDefaultMapData = useCallback(() => {
    // Set placeholder location immediately to render the section
    setValue('attributes.location', { lat: null, lng: null }, { shouldDirty: true });
    
    // Then update with actual location asynchronously
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setValue('attributes.location', {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }, { shouldDirty: true });
      },
      (error) => {
        // Keep the placeholder on error
        console.warn('Geolocation failed:', error);
        // Optionally, you could set a default location or keep the placeholder
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [setValue]);
  
  // Delete entire attribute section
  const deleteAttributeSection = useCallback((viewType) => {
    switch (viewType) {
      case RECORD_CONSTANTS.VIEW_TYPES.TASK:
        // Clear all task-related attributes
        setValue('attributes.isComplete', null, { shouldDirty: true });
        setValue('attributes.dueDate', null, { shouldDirty: true });
        setValue('attributes.priority', null, { shouldDirty: true });
        break;
      case RECORD_CONSTANTS.VIEW_TYPES.CALENDAR:
        // Clear calendar attributes (DateRange)
        setValue('attributes.date', null, { shouldDirty: true });
        break;
      case RECORD_CONSTANTS.VIEW_TYPES.MAP:
        // Clear location
        setValue('attributes.location', null, { shouldDirty: true });
        break;
      default:
        // No attributes to delete for other view types
        break;
    }
  }, [setValue]);
  
  // Handle view click - create default data or just scroll
  const handleViewClick = useCallback(
    (view) => {
      const hasData = userSelectedViews.has(view);
      let hasOverlay = false;

      if (view === RECORD_CONSTANTS.VIEW_TYPES.CALENDAR) {
        hasOverlay = Boolean(aiOverlay["attributes.date"]);
      } else if (view === RECORD_CONSTANTS.VIEW_TYPES.TASK) {
        hasOverlay = ["attributes.isComplete", "attributes.dueDate", "attributes.priority"].some(
          (path) => Boolean(aiOverlay[path])
        );
      } else if (view === RECORD_CONSTANTS.VIEW_TYPES.MAP) {
        hasOverlay = Boolean(aiOverlay["attributes.location"]);
      } else if (view === RECORD_CONSTANTS.VIEW_TYPES.GRAPH) {
        hasOverlay = Boolean(aiOverlay["links.outgoing"]);
      }

      if (!hasData && !hasOverlay) {
        // No data exists - create default values
        switch (view) {
          case RECORD_CONSTANTS.VIEW_TYPES.TASK:
            createDefaultTaskData();
            break;
          case RECORD_CONSTANTS.VIEW_TYPES.CALENDAR:
            createDefaultCalendarData();
            break;
          case RECORD_CONSTANTS.VIEW_TYPES.MAP:
            createDefaultMapData();
            break;
          default:
            break;
        }
      }

      // View actions are now handled by the parent component
    },
    [
      userSelectedViews,
      aiOverlay,
      createDefaultTaskData,
      createDefaultCalendarData,
      createDefaultMapData,
    ]
  );

  const validate = useCallback(async () => {
    // Trigger React Hook Form validation
    await trigger();
    
    // Additional custom validation
    const customValidationErrors = {};

    if (!formData.content.title.trim()) {
      customValidationErrors["content.title"] = createValidationError(
        "records.editor.validation.title_required",
        {},
        "Title is required"
      );
    }
    if (formData.content.title.length > 200) {
      customValidationErrors["content.title"] = createValidationError(
        "records.editor.validation.title_max_length",
        { max: 200 },
        "Title must be 200 characters or less"
      );
    }
    if (formData.content.tags.length > 50) {
      customValidationErrors["content.tags"] = createValidationError(
        "records.editor.validation.tags_max",
        { max: 50 },
        "Maximum 50 tags allowed"
      );
    }
    if (formData.content.attachments.length > RECORD_CONSTANTS.MAX_ATTACHMENTS) {
      customValidationErrors["content.attachments"] = createValidationError(
        "records.editor.validation.attachments_max",
        { max: RECORD_CONSTANTS.MAX_ATTACHMENTS },
        `Maximum ${RECORD_CONSTANTS.MAX_ATTACHMENTS} attachments allowed`
      );
    }

    setCustomErrors(customValidationErrors);
    
    // Return combined errors
    return { ...rhfErrors, ...customValidationErrors };
  }, [formData, trigger, rhfErrors]);

  // Validate a single field
  const validateField = useCallback(async (path, value) => {
    // Trigger React Hook Form validation for this field
    await trigger(path);
    
    let error = null;

    switch (path) {
      case "content.title":
        if (!value.trim()) {
          error = createValidationError(
            "records.editor.validation.title_required",
            {},
            "Title is required"
          );
        } else if (value.length > 200) {
          error = createValidationError(
            "records.editor.validation.title_max_length",
            { max: 200 },
            "Title must be 200 characters or less"
          );
        }
        break;
      case "content.tags":
        if (value.length > 50) {
          error = createValidationError(
            "records.editor.validation.tags_max",
            { max: 50 },
            "Maximum 50 tags allowed"
          );
        }
        break;
      case "content.attachments":
        if (value.length > RECORD_CONSTANTS.MAX_ATTACHMENTS) {
          error = createValidationError(
            "records.editor.validation.attachments_max",
            { max: RECORD_CONSTANTS.MAX_ATTACHMENTS },
            `Maximum ${RECORD_CONSTANTS.MAX_ATTACHMENTS} attachments allowed`
          );
        }
        break;
      case "attributes.dueDate":
        if (value !== null && value !== undefined) {
          if (!(value instanceof Date) && !value.toDate) {
            error = createValidationError(
              "records.editor.validation.due_date_invalid",
              {},
              "Due date must be a valid date"
            );
          } else {
            const date = value instanceof Date ? value : value.toDate();
            if (isNaN(date.getTime())) {
              error = createValidationError(
                "records.editor.validation.date_format_invalid",
                {},
                "Invalid date format"
              );
            }
          }
        }
        break;
      default:
        // No validation for other fields
        break;
    }

    setCustomErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[path] = error;
      } else {
        delete newErrors[path];
      }
      return newErrors;
    });

    return error;
  }, [trigger]);

  const reset = useCallback(() => {
    // Reset React Hook Form to initial values
    rhfReset(defaultValues);
    
    setCustomErrors({});
    setAi(null);
    dispatchAiAssistant({ type: "clear", keepPrompt: false });
  }, [defaultValues, rhfReset, dispatchAiAssistant, setAi]);

  // Helper to compute attachment changes for save
  const computeAttachmentChanges = useCallback(() => {
    const currentAttachmentIds = new Set(formData.content.attachments.map(att => att.id));
    const deletedAttachments = originalAttachments.filter(att => !currentAttachmentIds.has(att.id));
    const attachmentsToDelete = deletedAttachments.map(att => att.id);

    const tempAttachments = formData.content.attachments.filter(att => att.isTemp);

    return {
      toDelete: attachmentsToDelete,
      tempAttachments,
      deletedAttachments,
    };
  }, [formData.content.attachments, originalAttachments]);

  return {
    formData,
    isEditMode,
    hasChanges,
    userSelectedViews,
    ai,
    errors,
    isSaving,
    setIsSaving,
    updateField,
    handleViewClick,
    deleteAttributeSection,
    validate,
    validateField,
    reset,
    computeAttachmentChanges,
    aiAssistantState,
    setAiPrompt,
    setAiStatus,
    setAiProposal,
    clearAiProposal,
    applyAiProposal,
    aiOverlay,
  };
}
